import { notifications } from "@mantine/notifications";
import {
  QueryCache,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { callApi, callDownloadApi } from "./api";
import { appendSearchParams } from "./helper";
import { createWebStorageClient } from "@congrs/file-upload";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import { useLanguage } from "./LanguageContext";

// A 401 usually means the session is simply gone: the token expired, was never
// there (anonymous pages still mount the current-user query), or the account was
// deleted. The user is signed out and redirected to /login, so that is an expected
// state rather than an incident. The API's catch-all UNAUTHORIZED code is the
// exception — the token was rejected for a reason our client did not anticipate
// (malformed token, a route requiring auth that should not), so that one is worth
// a Sentry event. See `unauthorized()` in apps/api/src/auth/auth.plugin.js.
const isExpectedSignOut = (error) =>
  error?.status === 401 && error?.code !== "UNAUTHORIZED";

// Any other 4xx is an expected outcome too — a form failing validation, a
// disabled feature, a missing record — and the API keeps them out of Sentry as
// well. Reporting them also uploaded an on-error replay for each one. The user
// still sees the error toast; this only decides what reaches Sentry.
const isExpectedClientError = (error) => {
  const status = error?.status;
  if (status === 401) return isExpectedSignOut(error);
  return status >= 400 && status < 500;
};

// Add Sentry to the query client, so we can see errors in the Sentry dashboard
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isExpectedClientError(error)) return;

      Sentry.captureException(error, {
        tags: {
          feature: "data-fetching",
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      // Skip retries on deterministic 4xx (disabled feature, forbidden,
      // not-found); retry transient 5xx and network failures up to 3 times.
      retry: (failureCount, error) => {
        const status = error?.status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
    mutations: {
      onError: (error) => {
        if (isExpectedClientError(error)) return;

        Sentry.captureException(error, {
          tags: {
            feature: "data-mutation",
          },
        });
      },
    },
  },
});

const invalidate = ({ invalidateQueryKeys }) => {
  if (!invalidateQueryKeys) return queryClient.invalidateQueries();

  return Promise.all(
    invalidateQueryKeys.map((key) =>
      queryClient.invalidateQueries({
        queryKey: Array.isArray(key) ? key : [key],
      })
    )
  );
};

export const useApiInfiniteQuery = (config) => {
  const { language } = useLanguage();

  const { fetchOptions } = config;

  return useInfiniteQuery({
    initialPageParam: 1,
    ...config.queryOptions,
    queryFn: ({ pageParam }) => {
      const token = localStorage.getItem("token");

      const params = {
        language: language,
        page: pageParam,
        ...fetchOptions.params,
      };

      const url = appendSearchParams(fetchOptions.url, params);

      return callApi({
        url,
        method: fetchOptions.method,
        body: fetchOptions.body,
        token,
      });
    },
    getNextPageParam: (response) => {
      if (!response.pagination.nextPage) return undefined;
      return response.pagination.nextPage;
    },
  });
};

/**
 * @typedef {Object} FetchOptions
 * @property {string} url - The URL for the query.
 * @property {string} method - The HTTP method for the query.
 * @property {Object} body - The body of the request.
 * @property {Object} params - The query parameters.
 */

/**
 * Custom hook for making authenticated API queries using react-query.
 * @param {Object} config - The configuration for the query.
 * @param {import("@tanstack/react-query").UseQueryOptions} config.queryOptions - The configuration options for the query.
 * @param {FetchOptions} config.fetchOptions - The fetch options for the query.
 * @returns {import("@tanstack/react-query").UseQueryResult} - The result of the query.
 */
export const useApiQuery = (config) => {
  const { language } = useLanguage();

  const { fetchOptions } = config;

  // Inject language automatically
  const params = {
    language: language,
    ...fetchOptions.params,
  };

  if (params) {
    fetchOptions.url = appendSearchParams(fetchOptions.url, params);
  }

  return useQuery({
    ...config.queryOptions,
    queryFn: async () => {
      const token = localStorage.getItem("token");

      return callApi({
        url: fetchOptions.url,
        method: fetchOptions.method,
        body: fetchOptions.body,
        token,
      });
    },
  });
};

/**
 * Custom hook for making authenticated API mutations using react-query.
 * @param {Object} config - The configuration for the mutation.
 * @param {import("@tanstack/react-query").UseMutationOptions} config.mutationOptions - The configuration options for the mutation.
 * @param {FetchOptions} config.fetchOptions - The fetch options for the mutation.
 * @param {Array<Array>} config.invalidateQueryKeys - Query keys to invalidate after the mutation success. Pass an empty array to invalidate nothing; omit to invalidate every query.
 * @param {Boolean} config.hideNotification - Whether to hide the notification after the mutation.
 * @param {import("@mantine/notifications").NotificationsProps | ((data: any, variables: any) => import("@mantine/notifications").NotificationsProps)} config.successMessage - The success message to show after the mutation. A function receives the response and the request body, for mutations whose outcome or input decides the wording.
 * @returns {import("@tanstack/react-query").UseMutationResult} - The result of the mutation.
 */

export const useApiMutation = (config) => {
  const { fetchOptions } = config;

  if (fetchOptions.params) {
    fetchOptions.url = appendSearchParams(
      fetchOptions.url,
      fetchOptions.params
    );
  }

  return useMutation({
    ...config.mutationOptions,
    mutationFn: async (body) => {
      const token = localStorage.getItem("token");

      return callApi({
        url: fetchOptions.url,
        method: fetchOptions.method,
        body,
        token,
      });
    },
    onMutate: () => {
      if (config.loadingMessage) {
        return notifications.show({
          loading: true,
          autoClose: false,
          withCloseButton: false,
          ...config.loadingMessage,
        });
      }
    },
    onSuccess: (data, variables, context) => {
      const successMessage =
        typeof config.successMessage === "function"
          ? config.successMessage(data, variables)
          : config.successMessage;

      if (context) {
        notifications.update({
          id: context,
          loading: false,
          autoClose: true,
          color: "green",
          message: null,
          ...successMessage,
        });
      } else if (!config.hideNotification) {
        notifications.show({
          title: "Operation successful",
          color: "green",
          ...successMessage,
        });
      }

      // Capture mutation event in PostHog with page and environment
      const action = fetchOptions.url
        .split("/")[1]
        .concat("_", fetchOptions.method.toLowerCase());
      posthog?.capture(action, {
        page: fetchOptions.url,
        env: process.env.CLOUD_ENV,
      });

      invalidate(config);
    },
    onError: (error, variables, context) => {
      if (isExpectedSignOut(error)) return;

      const isSqlError = error.code && error.code.startsWith("ER_"); // MySQL errors usually start with 'ER_'

      const errorMessage = {
        title: "Error",
        color: "red",
        message: isSqlError
          ? config.errorMessages?.[error.code] || "Internal server error"
          : error.message,
      };

      if (context) {
        notifications.update({
          id: context,
          loading: false,
          autoClose: true,
          message: null,
          ...errorMessage,
        });
      } else if (!config.hideNotification) {
        notifications.show(errorMessage);
      }
    },
  });
};

/**
 * Custom hook for making authenticated API mutations that return a file download.
 * Parses headers to save the file locally.
 */
export const useApiDownloadMutation = (config) => {
  const { fetchOptions } = config;

  if (fetchOptions.params) {
    fetchOptions.url = appendSearchParams(
      fetchOptions.url,
      fetchOptions.params
    );
  }

  return useMutation({
    ...config.mutationOptions,
    mutationFn: async (body) => {
      const token = localStorage.getItem("token");

      return callDownloadApi({
        url: fetchOptions.url,
        method: fetchOptions.method || "POST",
        body,
        token,
      });
    },
    onMutate: () => {
      if (config.loadingMessage) {
        return notifications.show({
          loading: true,
          autoClose: false,
          withCloseButton: false,
          ...config.loadingMessage,
        });
      }
    },
    onSuccess: (data, variables, context) => {
      if (context) {
        notifications.update({
          id: context,
          loading: false,
          autoClose: true,
          color: "green",
          message: null,
          ...config.successMessage,
        });
      } else if (!config.hideNotification) {
        const successMessage = {
          title: "Download successful",
          color: "green",
          ...config.successMessage,
        };

        notifications.show(successMessage);
      }

      // Capture mutation event in PostHog with page and environment
      const action = fetchOptions.url
        .split("/")[1]
        .concat(
          "_",
          fetchOptions.method ? fetchOptions.method.toLowerCase() : "post",
          "_download"
        );
      posthog?.capture(action, {
        page: fetchOptions.url,
        env: process.env.CLOUD_ENV,
      });

      invalidate(config);
    },
    onError: (error, variables, context) => {
      if (isExpectedSignOut(error)) return;

      const isSqlError = error.code && error.code.startsWith("ER_"); // MySQL errors usually start with 'ER_'

      const errorMessage = {
        title: "Error downloading file",
        color: "red",
        message: isSqlError
          ? config.errorMessages?.[error.code] || "Internal server error"
          : error.message,
      };

      if (context) {
        notifications.update({
          id: context,
          loading: false,
          autoClose: true,
          message: null,
          ...errorMessage,
        });
      } else if (!config.hideNotification) {
        notifications.show(errorMessage);
      }
    },
  });
};

export const useFileUpload = () => {
  const token = localStorage.getItem("token");

  const storage = createWebStorageClient("/api", token);

  return useMutation({
    mutationFn: async (file) => {
      return storage.uploadFile(file);
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        color: "red",
        message: error.message,
      });
    },
  });
};

export const useLocalizedApiQuery = (config) => {
  const { language } = useLanguage();

  return useApiQuery({
    ...config,
    queryOptions: {
      ...config.queryOptions,
      queryKey: [...(config.queryOptions.queryKey || []), language],
    },
    fetchOptions: {
      ...config.fetchOptions,
      params: {
        ...config.fetchOptions.params,
        language,
      },
    },
  });
};
